import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { WEEKDAYS, getTemplateById, calculateAgeYears } from '../lib/curriculumRhythmTemplates';
import { buildRoutineTimetableForStudent, isRoutineTimetable } from '../lib/routineSlotFiller';

const RoutineTimetableView = ({
  activities,
  student,
  termId,
  savedTimetable,
  templateId,
  onSave,
  saving,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [timetable, setTimetable] = useState(null);

  const ncStatus = student?.questionnaireAnswers?.ncStatus;
  const answers = student?.questionnaireAnswers;
  const ageYears = calculateAgeYears(student?.dob);

  useEffect(() => {
    if (savedTimetable && isRoutineTimetable(savedTimetable)) {
      setTimetable(savedTimetable);
      return;
    }
    if (activities?.length && student?.id && termId) {
      const built = buildRoutineTimetableForStudent(
        activities,
        ncStatus,
        answers,
        ageYears,
        student.id,
        termId,
        templateId || savedTimetable?.templateId
      );
      setTimetable(built);
    }
  }, [activities, student, termId, savedTimetable, templateId, ncStatus, answers, ageYears]);

  const template = useMemo(
    () => (timetable?.templateId ? getTemplateById(timetable.templateId) : null),
    [timetable?.templateId]
  );

  const anchorOrder = useMemo(() => {
    if (!template) return Object.keys(timetable?.anchors || {});
    return template.slots
      .filter((s) => s.kind === 'anchor')
      .sort((a, b) => a.order - b.order)
      .map((s) => s.id);
  }, [template, timetable]);

  const variableOrder = useMemo(() => {
    if (!template) return Object.keys(timetable?.variables || {});
    return template.slots
      .filter((s) => s.kind === 'variable')
      .sort((a, b) => a.order - b.order)
      .map((s) => s.id);
  }, [template, timetable]);

  const updateAnchorActivity = (slotId, activityIndex, field, value) => {
    setTimetable((prev) => {
      const slot = prev.anchors[slotId];
      if (!slot) return prev;
      const activitiesList = [...(slot.activities || [])];
      activitiesList[activityIndex] = { ...activitiesList[activityIndex], [field]: value };
      return {
        ...prev,
        anchors: { ...prev.anchors, [slotId]: { ...slot, activities: activitiesList } },
      };
    });
  };

  const setAnchorFromMatched = (slotId, matchedActivity) => {
    setTimetable((prev) => {
      const slot = prev.anchors[slotId];
      if (!slot) return prev;
      return {
        ...prev,
        anchors: {
          ...prev.anchors,
          [slotId]: {
            ...slot,
            activities: [
              {
                kbItemId: matchedActivity.kbItemId,
                title: matchedActivity.title,
                description: matchedActivity.description,
                duration: matchedActivity.recommendedFrequency?.sessionMinutes || 15,
                score: matchedActivity.score,
                targetDomains: matchedActivity.targetDomains,
              },
            ],
          },
        },
      };
    });
  };

  const toggleVariableDay = (slotId, day) => {
    setTimetable((prev) => {
      const slot = prev.variables[slotId];
      if (!slot) return prev;
      const days = new Set(slot.scheduledDays || []);
      if (days.has(day)) days.delete(day);
      else days.add(day);
      const scheduledDays = WEEKDAYS.filter((d) => days.has(d));
      const byDay = { ...slot.byDay };
      if (!days.has(day)) delete byDay[day];
      else if (!byDay[day]) byDay[day] = slot.byDay?.[day] || [];
      return {
        ...prev,
        variables: {
          ...prev.variables,
          [slotId]: { ...slot, scheduledDays, byDay },
        },
      };
    });
  };

  const setVariableDayActivity = (slotId, day, matchedActivity) => {
    setTimetable((prev) => {
      const slot = prev.variables[slotId];
      if (!slot) return prev;
      return {
        ...prev,
        variables: {
          ...prev.variables,
          [slotId]: {
            ...slot,
            byDay: {
              ...slot.byDay,
              [day]: [
                {
                  kbItemId: matchedActivity.kbItemId,
                  title: matchedActivity.title,
                  description: matchedActivity.description,
                  duration: matchedActivity.recommendedFrequency?.sessionMinutes || 15,
                  score: matchedActivity.score,
                },
              ],
            },
          },
        },
      };
    });
  };

  const handleSave = () => {
    if (timetable && onSave) {
      onSave({ ...timetable, updatedAt: new Date().toISOString() });
      setIsEditing(false);
    }
  };

  if (!timetable) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          No routine timetable available. Add matched activities first.
        </CardContent>
      </Card>
    );
  }

  const renderActivityCell = (acts) => {
    if (!acts?.length) {
      return <span className="text-gray-400 text-sm italic">Not assigned</span>;
    }
    return acts.map((a, i) => (
      <div key={i} className="text-sm">
        <span className="font-medium text-gray-900">{a.title}</span>
        {a.duration ? <span className="text-gray-500 ml-1">({a.duration} min)</span> : null}
      </div>
    ));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-600">
            Template: <strong>{timetable.templateLabel}</strong> — anchors repeat every weekday; only
            variable slots change across the week.
          </p>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Edit routine
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save routine'}
              </button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily anchors (every weekday)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {anchorOrder.map((slotId) => {
              const slot = timetable.anchors[slotId];
              if (!slot) return null;
              const def = template?.slots?.find((s) => s.id === slotId);
              return (
                <div
                  key={slotId}
                  className="flex flex-col sm:flex-row sm:items-start gap-2 p-3 bg-indigo-50/50 rounded-lg border border-indigo-100"
                >
                  <div className="sm:w-1/3">
                    <p className="font-medium text-gray-900">{slot.label}</p>
                    {def?.notes && <p className="text-xs text-gray-500 mt-0.5">{def.notes}</p>}
                  </div>
                  <div className="flex-1">
                    {isEditing ? (
                      <div className="space-y-2">
                        {renderActivityCell(slot.activities)}
                        <select
                          className="w-full text-sm border rounded px-2 py-1"
                          value={slot.activities?.[0]?.kbItemId || ''}
                          onChange={(e) => {
                            const act = activities.find((a) => a.kbItemId === e.target.value);
                            if (act) setAnchorFromMatched(slotId, act);
                          }}
                        >
                          <option value="">Swap activity…</option>
                          {activities.map((a) => (
                            <option key={a.kbItemId} value={a.kbItemId}>
                              {a.title}
                            </option>
                          ))}
                        </select>
                        {slot.activities?.[0] && (
                          <input
                            type="text"
                            className="w-full text-sm border rounded px-2 py-1"
                            value={slot.activities[0].title}
                            onChange={(e) => updateAnchorActivity(slotId, 0, 'title', e.target.value)}
                            placeholder="Display title"
                          />
                        )}
                      </div>
                    ) : (
                      renderActivityCell(slot.activities)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Weekly variations</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left p-2 border-b bg-gray-50 w-40">Slot</th>
                {WEEKDAYS.map((d) => (
                  <th key={d} className="p-2 border-b bg-gray-50 text-center min-w-[100px]">
                    {d.slice(0, 3)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {variableOrder.map((slotId) => {
                const slot = timetable.variables[slotId];
                if (!slot) return null;
                return (
                  <tr key={slotId} className="border-b">
                    <td className="p-2 font-medium text-gray-800 align-top">{slot.label}</td>
                    {WEEKDAYS.map((day) => {
                      const scheduled = (slot.scheduledDays || []).includes(day);
                      const dayActs = slot.byDay?.[day];
                      return (
                        <td
                          key={day}
                          className={`p-2 align-top text-center ${scheduled ? 'bg-amber-50/80' : 'bg-gray-50/50'}`}
                        >
                          {isEditing && (
                            <label className="flex justify-center mb-1">
                              <input
                                type="checkbox"
                                checked={scheduled}
                                onChange={() => toggleVariableDay(slotId, day)}
                                className="mr-1"
                              />
                              <span className="text-xs text-gray-500">On</span>
                            </label>
                          )}
                          {scheduled ? (
                            isEditing ? (
                              <select
                                className="w-full text-xs border rounded px-1 py-0.5"
                                value={dayActs?.[0]?.kbItemId || ''}
                                onChange={(e) => {
                                  const act = activities.find((a) => a.kbItemId === e.target.value);
                                  if (act) setVariableDayActivity(slotId, day, act);
                                }}
                              >
                                <option value="">—</option>
                                {activities.map((a) => (
                                  <option key={a.kbItemId} value={a.kbItemId}>
                                    {a.title}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              renderActivityCell(dayActs)
                            )
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoutineTimetableView;
